import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadTwoToneIcon from "@mui/icons-material/FileDownloadTwoTone";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DonutLargeTwoToneIcon from "@mui/icons-material/DonutLargeTwoTone";
import LabelImportantTwoToneIcon from "@mui/icons-material/LabelImportantTwoTone";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import type {
  CustomColumn,
  CustomColumnValue,
  FilterOperator,
  Priority,
  RoomStatus,
  Tag,
  User,
} from "../types/types.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { isoToYmd } from "../utils/date.ts";

interface TaskExportDialogProps {
  onClose: () => void;
  /** 選択候補となるアクティブなプロジェクト一覧 */
  todoLists: { id: string; name: string }[];
  roomMembers: User[];
  roomStatuses: RoomStatus[];
  availableTags: Tag[];
  color: PaletteColor;
  roomId: number;
  honbuId: number;
}

/** 出力1行分のデータ（列順は buildHeaders と対応。customValues はカスタム列の値） */
interface ExportRow {
  taskId: string;
  parentTaskId: string;
  parentTitle: string;
  title: string;
  status: string;
  priority: string;
  startDate: string;
  endDate: string;
  assignees: string;
  requesters: string;
  tags: string;
  memo: string;
  createdAt: string;
  completedAt: string;
  completedBy: string;
  customValues: string[];
}

const BASE_HEADERS = [
  "タスクID",
  "親タスクID",
  "親タスク名",
  "タスク名",
  "ステータス",
  "重要度",
  "開始日",
  "終了日",
  "担当者",
  "依頼者",
  "タグ",
  "メモ",
  "作成日",
  "完了日",
  "完了者",
];

const PRIORITY_OPTIONS: Priority[] = ["高", "中", "低", "なし"];
const DEFAULT_STATUS_OPTIONS = ["未対応", "対応中", "待機", "完了", "非表示"];

/** OR / AND の切替（FilterDialog と同じ「または / かつ」ラジオ） */
const OperatorToggle = ({
  value,
  onChange,
  name,
}: {
  value: FilterOperator;
  onChange: (op: FilterOperator) => void;
  name: string;
}) => (
  <div className="flex items-center space-x-2 font-normal">
    <label className="text-xs flex items-center cursor-pointer">
      <input
        type="radio"
        name={`export-op-${name}`}
        checked={value === "OR"}
        onChange={() => onChange("OR")}
        className="mr-1"
      />
      または
    </label>
    <label className="text-xs flex items-center cursor-pointer">
      <input
        type="radio"
        name={`export-op-${name}`}
        checked={value === "AND"}
        onChange={() => onChange("AND")}
        className="mr-1"
      />
      かつ
    </label>
  </div>
);

const rowToArray = (r: ExportRow): string[] => [
  r.taskId,
  r.parentTaskId,
  r.parentTitle,
  r.title,
  r.status,
  r.priority,
  r.startDate,
  r.endDate,
  r.assignees,
  r.requesters,
  r.tags,
  r.memo,
  r.createdAt,
  r.completedAt,
  r.completedBy,
  ...r.customValues,
];

/** CSV のフィールドエスケープ（カンマ・改行・ダブルクォートを含む場合はクォート） */
const escapeCsvField = (value: string): string => {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/** ファイル名に使えない文字を除去する */
const sanitizeFileName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]/g, "_");

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

const exportAsCsv = (
  headers: string[],
  rows: ExportRow[],
  fileName: string,
) => {
  const lines = [
    headers.map(escapeCsvField).join(","),
    ...rows.map((r) => rowToArray(r).map(escapeCsvField).join(",")),
  ];
  // Excel でそのまま開けるよう UTF-8 BOM を付与する
  const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
  downloadBlob(
    new Blob([bom, lines.join("\r\n")], { type: "text/csv" }),
    fileName,
  );
};

const exportAsExcel = async (
  headers: string[],
  rows: ExportRow[],
  fileName: string,
) => {
  // xlsx はサイズが大きいため、Excel 出力時のみ動的ロードする
  const XLSX = await import("xlsx");
  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows.map(rowToArray)]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "タスク一覧");
  XLSX.writeFile(workbook, fileName);
};

/**
 * タスクデータ出力ダイアログ。
 * Step1: アクティブなプロジェクトから出力対象を1つ選択
 * Step2: メインタスク・サブタスクそれぞれの出力条件と形式を指定して出力
 */
export function TaskExportDialog({
  onClose,
  todoLists,
  roomMembers,
  roomStatuses,
  availableTags,
  color,
  roomId,
  honbuId,
}: TaskExportDialogProps) {
  const isDarkMode = useDarkMode();
  const accent = PaletteColorHex[color];

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  // 選択中プロジェクトのカスタム列（出力列・絞り込みに使用）
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);

  // メインタスクの出力条件（空選択 = 条件なし = 全て対象）
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<Priority[]>([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [assigneeOperator, setAssigneeOperator] =
    useState<FilterOperator>("OR");
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagOperator, setTagOperator] = useState<FilterOperator>("OR");
  /** カスタム列（select型）の絞り込み: columnId -> 選択した選択肢の値 */
  const [customColumnFilters, setCustomColumnFilters] = useState<
    Record<number, string[]>
  >({});
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");

  // サブタスクの出力条件（サブタスクが持つ項目のみ）
  const [includeSubtasks, setIncludeSubtasks] = useState(true);
  const [subStatuses, setSubStatuses] = useState<string[]>([]);
  const [subAssigneeIds, setSubAssigneeIds] = useState<string[]>([]);
  const [subAssigneeOperator, setSubAssigneeOperator] =
    useState<FilterOperator>("OR");
  const [subPeriodStart, setSubPeriodStart] = useState("");
  const [subPeriodEnd, setSubPeriodEnd] = useState("");

  const [format, setFormat] = useState<"csv" | "excel">("csv");
  const [isExporting, setIsExporting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const statusOptions = useMemo(() => {
    const names = roomStatuses.map((s) => s.statusName);
    return names.length > 0 ? names : DEFAULT_STATUS_OPTIONS;
  }, [roomStatuses]);

  const selectedListName =
    todoLists.find((l) => l.id === selectedListId)?.name ?? "";

  // プロジェクト選択が変わったらカスタム列を取得し直し、カスタム列の絞り込みをリセットする
  useEffect(() => {
    setCustomColumnFilters({});
    if (!selectedListId) {
      setCustomColumns([]);
      return;
    }
    let aborted = false;
    fetch(`/api/todolists/${selectedListId}/custom-columns?honbuId=${honbuId}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: CustomColumn[]) => {
        if (!aborted) setCustomColumns(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!aborted) setCustomColumns([]);
      });
    return () => {
      aborted = true;
    };
  }, [selectedListId, honbuId]);

  // select 型のカスタム列のみ絞り込み対象にする
  const selectableCustomColumns = useMemo(
    () =>
      customColumns.filter(
        (c) => c.columnType === "select" && c.options.length > 0,
      ),
    [customColumns],
  );

  const toggleInArray = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const toggleCustomColumnFilter = (columnId: number, value: string) => {
    setCustomColumnFilters((prev) => ({
      ...prev,
      [columnId]: toggleInArray(prev[columnId] ?? [], value),
    }));
  };

  /** 期間（開始・終了）とタスク期間の重なり判定（既存の詳細フィルターと同じ挙動） */
  const matchesPeriod = (
    task: any,
    filterStart: string,
    filterEnd: string,
  ): boolean => {
    if (!filterStart && !filterEnd) return true;
    // 開始日がないタスクは期間指定時に除外する
    if (!task.startDate) return false;
    const taskStart = isoToYmd(task.startDate);
    const taskEnd = isoToYmd(task.endDate || task.startDate);
    if (filterStart && taskEnd < filterStart) return false;
    if (filterEnd && taskStart > filterEnd) return false;
    return true;
  };

  const matchesStatuses = (task: any, statuses: string[]): boolean =>
    statuses.length === 0 || statuses.includes(task.status || "未対応");

  const matchesAssignees = (
    task: any,
    assigneeIds: string[],
    operator: FilterOperator,
  ): boolean => {
    if (assigneeIds.length === 0) return true;
    const ids = new Set(
      (task.taskAssignees || []).map((a: any) => String(a.assigneeHonbuId)),
    );
    return operator === "AND"
      ? assigneeIds.every((id) => ids.has(id))
      : assigneeIds.some((id) => ids.has(id));
  };

  /** メインタスクが条件に合致するか（Deleted は常に除外） */
  const matchesMainConditions = (
    task: any,
    customValueMap: Map<string, string>,
  ): boolean => {
    if (task.status === "Deleted") return false;
    if (!matchesStatuses(task, selectedStatuses)) return false;
    if (
      selectedPriorities.length > 0 &&
      !selectedPriorities.includes((task.priority || "なし") as Priority)
    )
      return false;
    if (!matchesAssignees(task, selectedAssigneeIds, assigneeOperator))
      return false;
    if (selectedTagIds.length > 0) {
      const ids = new Set(
        (task.taskTags || []).map((t: any) => Number(t.tagId)),
      );
      const tagMatch =
        tagOperator === "AND"
          ? selectedTagIds.every((id) => ids.has(id))
          : selectedTagIds.some((id) => ids.has(id));
      if (!tagMatch) return false;
    }
    // カスタム列（select型）: 同一列内は OR、列をまたぐ場合は AND（既存フィルターと同じ）
    for (const [columnId, values] of Object.entries(customColumnFilters)) {
      if (values.length === 0) continue;
      const actual = (
        customValueMap.get(`${task.taskId}:${columnId}`) ?? ""
      ).toLowerCase();
      if (!values.some((v) => v.toLowerCase() === actual)) return false;
    }
    if (!matchesPeriod(task, periodStart, periodEnd)) return false;
    return true;
  };

  /** サブタスクが条件に合致するか（サブタスクはステータス・担当者・期間のみ保持） */
  const matchesSubConditions = (task: any): boolean => {
    if (task.status === "Deleted") return false;
    if (!matchesStatuses(task, subStatuses)) return false;
    if (!matchesAssignees(task, subAssigneeIds, subAssigneeOperator))
      return false;
    if (!matchesPeriod(task, subPeriodStart, subPeriodEnd)) return false;
    return true;
  };

  const toRow = (
    task: any,
    customValueMap: Map<string, string>,
    parent?: any,
  ): ExportRow => ({
    taskId: String(task.taskId),
    parentTaskId: parent ? String(parent.taskId) : "",
    parentTitle: parent ? parent.title || "" : "",
    title: task.title || "",
    status: task.status || "未対応",
    priority: task.priority || "なし",
    startDate: isoToYmd(task.startDate),
    endDate: isoToYmd(task.endDate),
    assignees: (task.taskAssignees || [])
      .map((a: any) => a.name)
      .join("、"),
    requesters: (task.taskRequesters || [])
      .map((r: any) => r.name)
      .join("、"),
    tags: (task.taskTags || []).map((t: any) => t.name).join("、"),
    memo: task.memo || "",
    createdAt: isoToYmd(task.createdAt),
    completedAt: isoToYmd(task.completedAt),
    completedBy: task.completedBy?.name || "",
    customValues: customColumns.map(
      (c) => customValueMap.get(`${task.taskId}:${c.customColumnId}`) ?? "",
    ),
  });

  const handleExport = async () => {
    if (!selectedListId) return;
    setIsExporting(true);
    setMessage(null);
    try {
      // 完了・非表示タスクも出力対象にできるよう includeHidden=true で取得する
      const fetchLists = fetch(
        `/api/todolists?roomId=${roomId}&honbuId=${honbuId}&includeHidden=true`,
      );
      // カスタム列の値（出力列・絞り込みに使用）
      const fetchValues =
        customColumns.length > 0
          ? fetch(`/api/todolists/${selectedListId}/custom-column-values`)
          : null;
      const [listRes, valuesRes] = await Promise.all([
        fetchLists,
        fetchValues,
      ]);

      if (!listRes.ok) throw new Error(`HTTP ${listRes.status}`);
      const data = await listRes.json();
      const list = (data.lists || []).find(
        (l: any) => String(l.todoListId) === selectedListId,
      );
      if (!list) {
        setMessage("プロジェクトが見つかりませんでした。");
        return;
      }

      // "taskId:customColumnId" -> 値 のマップを作る
      const customValueMap = new Map<string, string>();
      if (valuesRes && valuesRes.ok) {
        const values: CustomColumnValue[] = await valuesRes.json();
        for (const v of values) {
          customValueMap.set(
            `${v.taskId}:${v.customColumnId}`,
            v.value ?? "",
          );
        }
      }

      const rows: ExportRow[] = [];
      for (const task of list.tasks || []) {
        if (task.status === "Deleted") continue;
        if (matchesMainConditions(task, customValueMap)) {
          rows.push(toRow(task, customValueMap));
        }
        if (includeSubtasks) {
          for (const child of task.childTasks || []) {
            if (matchesSubConditions(child)) {
              rows.push(toRow(child, customValueMap, task));
            }
          }
        }
      }

      if (rows.length === 0) {
        setMessage("条件に合うタスクがありませんでした。");
        return;
      }

      const headers = [...BASE_HEADERS, ...customColumns.map((c) => c.columnName)];
      const ymd = isoToYmd(new Date().toISOString()).replace(/-/g, "");
      const base = `タスク出力_${sanitizeFileName(selectedListName)}_${ymd}`;
      if (format === "excel") {
        await exportAsExcel(headers, rows, `${base}.xlsx`);
      } else {
        exportAsCsv(headers, rows, `${base}.csv`);
      }
      onClose();
    } catch (error) {
      console.error("Failed to export tasks:", error);
      setMessage("出力に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setIsExporting(false);
    }
  };

  // --- スタイルヘルパー ---
  const groupHeadingClass = `text-sm font-bold pb-1 border-b ${
    isDarkMode
      ? "text-gray-200 border-gray-700"
      : "text-gray-800 border-gray-200"
  }`;
  const sectionLabelClass = `flex items-center gap-1 text-xs font-bold mb-1.5 ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  }`;
  const chipClass = (selected: boolean) =>
    `px-2.5 py-1 rounded-full text-xs border transition ${
      selected
        ? "text-white border-transparent"
        : isDarkMode
          ? "bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
    }`;
  const chipStyle = (selected: boolean) =>
    selected ? { backgroundColor: accent } : undefined;
  const dateInputClass = `border rounded-md px-2 py-1.5 text-sm ${
    isDarkMode
      ? "bg-gray-800 border-gray-600 text-gray-200"
      : "bg-white border-gray-300 text-gray-800"
  }`;

  /** チップ複数選択セクション（アイコン + ラベル + チップ群。extra は見出し右側に表示） */
  const renderChipSection = <T extends string | number>(
    label: string,
    icon: React.ReactNode,
    options: { value: T; label: string }[],
    selected: T[],
    onToggle: (value: T) => void,
    extra?: React.ReactNode,
  ) => (
    <div>
      <div className={`${sectionLabelClass} justify-between`}>
        <span className="flex items-center gap-1">
          {icon}
          <span>{label}</span>
        </span>
        {extra}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => onToggle(opt.value)}
              className={chipClass(isSelected)}
              style={chipStyle(isSelected)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderPeriodSection = (
    label: string,
    start: string,
    end: string,
    onStartChange: (v: string) => void,
    onEndChange: (v: string) => void,
  ) => (
    <div>
      <div className={sectionLabelClass}>
        <EventTwoToneIcon sx={{ fontSize: 16 }} />
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={start}
          onChange={(e) => onStartChange(e.target.value)}
          className={dateInputClass}
          aria-label={`${label}の開始日`}
        />
        <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          〜
        </span>
        <input
          type="date"
          value={end}
          onChange={(e) => onEndChange(e.target.value)}
          className={dateInputClass}
          aria-label={`${label}の終了日`}
        />
      </div>
    </div>
  );

  const showAssigneeFilter = roomId !== 0 && roomMembers.length > 0;
  const memberOptions = roomMembers.map((m) => ({
    value: m.id,
    label: m.name,
  }));
  const statusChipOptions = statusOptions.map((s) => ({ value: s, label: s }));

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`w-full max-w-lg max-h-[85vh] rounded-lg shadow-xl flex flex-col overflow-hidden border-2 ${
          isDarkMode ? "bg-gray-900 border-black" : "bg-white border-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー（PaletteColor グラデーション） */}
        <div
          className="flex-shrink-0 flex items-center justify-between p-4"
          style={{
            backgroundImage: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          }}
        >
          <h3 className="font-bold text-white flex items-center gap-2">
            <FileDownloadTwoToneIcon fontSize="small" />
            タスクデータ出力
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-white hover:bg-white/20"
            aria-label="閉じる"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* ステップ表示 */}
        <div
          className={`flex-shrink-0 px-4 py-2 text-sm border-b ${
            isDarkMode
              ? "text-gray-400 bg-gray-800 border-gray-700"
              : "text-gray-500 bg-gray-50 border-gray-200"
          }`}
        >
          {step === 1
            ? "① 出力するプロジェクトを1つ選択してください"
            : step === 2
              ? `② メインタスクの条件を指定してください（${selectedListName}）`
              : `③ サブタスクの条件を指定してください（${selectedListName}）`}
        </div>

        {/* 本体 */}
        <div className="flex-1 overflow-y-auto p-4">
          {step === 1 ? (
            <div className="space-y-1">
              {todoLists.length === 0 ? (
                <div
                  className={`p-8 text-center text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  出力できるプロジェクトがありません
                </div>
              ) : (
                todoLists.map((list) => {
                  const selected = selectedListId === list.id;
                  return (
                    <button
                      key={list.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setSelectedListId(list.id)}
                      className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-left text-sm transition ${
                        isDarkMode
                          ? "text-gray-200 hover:bg-gray-800"
                          : "text-gray-800 hover:bg-gray-100"
                      }`}
                      style={
                        selected
                          ? { backgroundColor: `${accent}22` }
                          : undefined
                      }
                    >
                      {selected ? (
                        <RadioButtonCheckedIcon
                          fontSize="small"
                          style={{ color: accent }}
                        />
                      ) : (
                        <RadioButtonUncheckedIcon
                          fontSize="small"
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        />
                      )}
                      <span className="truncate">{list.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                未選択の項目は絞り込みなし（全て出力）になります。
              </p>

              {/* メインタスクの条件 */}
              <div className={groupHeadingClass}>メインタスクの条件</div>

              {renderChipSection(
                "ステータス",
                <DonutLargeTwoToneIcon sx={{ fontSize: 16 }} />,
                statusChipOptions,
                selectedStatuses,
                (v) => setSelectedStatuses((prev) => toggleInArray(prev, v)),
              )}

              {renderChipSection(
                "重要度",
                <LabelImportantTwoToneIcon sx={{ fontSize: 16 }} />,
                PRIORITY_OPTIONS.map((p) => ({ value: p, label: p })),
                selectedPriorities,
                (v) =>
                  setSelectedPriorities((prev) =>
                    toggleInArray(prev, v as Priority),
                  ),
              )}

              {/* 担当者（MyTODO ではメンバーがいないため非表示） */}
              {showAssigneeFilter &&
                renderChipSection(
                  "担当者",
                  <PeopleTwoToneIcon sx={{ fontSize: 16 }} />,
                  memberOptions,
                  selectedAssigneeIds,
                  (v) =>
                    setSelectedAssigneeIds((prev) => toggleInArray(prev, v)),
                  <OperatorToggle
                    value={assigneeOperator}
                    onChange={setAssigneeOperator}
                    name="main-assignee"
                  />,
                )}

              {availableTags.length > 0 &&
                renderChipSection(
                  "タグ",
                  <StyleTwoToneIcon sx={{ fontSize: 16 }} />,
                  availableTags.map((t) => ({ value: t.id, label: t.name })),
                  selectedTagIds,
                  (v) => setSelectedTagIds((prev) => toggleInArray(prev, v)),
                  <OperatorToggle
                    value={tagOperator}
                    onChange={setTagOperator}
                    name="main-tag"
                  />,
                )}

              {/* カスタム列（select型のみ絞り込み対象。text型は出力のみ） */}
              {selectableCustomColumns.map((col) =>
                renderChipSection(
                  `${col.columnName}（カスタム項目）`,
                  <ExtensionTwoToneIcon sx={{ fontSize: 16 }} />,
                  col.options.map((o) => ({
                    value: o.optionValue,
                    label: o.optionValue,
                  })),
                  customColumnFilters[col.customColumnId] ?? [],
                  (v) => toggleCustomColumnFilter(col.customColumnId, v),
                ),
              )}

              {renderPeriodSection(
                "期間（タスクの期間と重なるものを出力）",
                periodStart,
                periodEnd,
                setPeriodStart,
                setPeriodEnd,
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                未選択の項目は絞り込みなし（全て出力）になります。
              </p>

              {/* サブタスクの条件 */}
              <div
                className={`${groupHeadingClass} flex items-center justify-between`}
              >
                <span>サブタスクの条件</span>
                <button
                  type="button"
                  onClick={() => setIncludeSubtasks((prev) => !prev)}
                  className={`flex items-center gap-1.5 text-xs font-normal ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {includeSubtasks ? (
                    <CheckBoxIcon fontSize="small" style={{ color: accent }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon
                      fontSize="small"
                      className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                    />
                  )}
                  サブタスクも出力する
                </button>
              </div>

              {includeSubtasks ? (
                <>
                  {renderChipSection(
                    "ステータス",
                    <DonutLargeTwoToneIcon sx={{ fontSize: 16 }} />,
                    statusChipOptions,
                    subStatuses,
                    (v) => setSubStatuses((prev) => toggleInArray(prev, v)),
                  )}

                  {showAssigneeFilter &&
                    renderChipSection(
                      "担当者",
                      <PeopleTwoToneIcon sx={{ fontSize: 16 }} />,
                      memberOptions,
                      subAssigneeIds,
                      (v) =>
                        setSubAssigneeIds((prev) => toggleInArray(prev, v)),
                      <OperatorToggle
                        value={subAssigneeOperator}
                        onChange={setSubAssigneeOperator}
                        name="sub-assignee"
                      />,
                    )}

                  {renderPeriodSection(
                    "期間（タスクの期間と重なるものを出力）",
                    subPeriodStart,
                    subPeriodEnd,
                    setSubPeriodStart,
                    setSubPeriodEnd,
                  )}
                </>
              ) : (
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  サブタスクは出力されません。
                </p>
              )}

              {message && (
                <div
                  className={`text-sm rounded-md p-2.5 ${
                    isDarkMode
                      ? "bg-red-900/30 text-red-300"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {message}
                </div>
              )}
            </div>
          )}
        </div>

        {/* フッター（出力形式は常に表示） */}
        <div
          className={`flex-shrink-0 flex items-center justify-between gap-3 p-4 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <span
              className={`text-xs font-bold ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              出力形式
            </span>
            {(
              [
                { value: "csv", label: "CSV" },
                { value: "excel", label: "Excel" },
              ] as const
            ).map((opt) => {
              const selected = format === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormat(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition ${
                    selected
                      ? "text-white border-transparent"
                      : isDarkMode
                        ? "bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  style={chipStyle(selected)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-3">
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                キャンセル
              </button>
              <button
                type="button"
                disabled={!selectedListId}
                onClick={() => setStep(2)}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: accent }}
              >
                次へ
              </button>
            </>
          ) : step === 2 ? (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                戻る
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition hover:brightness-110"
                style={{ backgroundColor: accent }}
              >
                次へ
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setMessage(null);
                  setStep(2);
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                戻る
              </button>
              <button
                type="button"
                disabled={isExporting}
                onClick={handleExport}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: accent }}
              >
                {isExporting ? "出力中..." : "出力する"}
              </button>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
